import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ExifReader from "exifreader";
import { createC2pa } from "@contentauth/c2pa-web/inline";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  FaCheckCircle,
  FaCopy,
  FaDownload,
  FaExclamationTriangle,
  FaFileImage,
  FaMapMarkerAlt,
  FaRedo,
  FaSearch,
  FaShieldAlt,
  FaUpload,
} from "react-icons/fa";

const MAX_FILE_SIZE = 25 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = [
  "jpg",
  "jpeg",
  "jxl",
  "png",
  "webp",
  "gif",
  "tif",
  "tiff",
  "heic",
  "heif",
  "avif",
];

const COLORS = {
  bg: "#F6F4F0",
  ink: "#17161B",
  panel: "#FFFFFF",
  dark: "#111014",
  accent: "#E8481D",
  accentSoft: "#FCE7DF",
  green: "#16845B",
  greenSoft: "#E7F6EF",
  yellow: "#A76500",
  yellowSoft: "#FFF4D6",
  blue: "#2366D1",
  blueSoft: "#EAF1FE",
  muted: "#77737F",
  border: "#E7E3DC",
};

const STRONG_AI_PATTERNS = [
  ["IPTC trained algorithmic media", /trainedalgorithmicmedia/i],
  ["Composite with synthetic elements", /compositesynthetic/i],
  ["AI-generated declaration", /\b(ai[-_ ]?generated|generated[-_ ]?by[-_ ]?ai)\b/i],
  ["Stable Diffusion metadata", /stable[ -]?diffusion|automatic1111|a1111|invokeai/i],
  ["ComfyUI workflow", /comfyui|workflow_api|ksampler/i],
  ["Midjourney metadata", /midjourney/i],
  ["Adobe Firefly metadata", /adobe[ -]?firefly|generative[ -]?fill/i],
  ["OpenAI image metadata", /dall[ -]?e|openai[ -]?(image|generator)/i],
  ["Flux image model metadata", /\bflux[ ._-]?(1|dev|schnell|pro)\b/i],
  ["Diffusion-generation parameters", /negative prompt|cfg scale|sampler name|model hash|denoising strength/i],
];

const EDITOR_PATTERNS = [
  ["Adobe Photoshop", /adobe photoshop/i],
  ["Adobe Lightroom", /adobe lightroom|lightroom classic/i],
  ["GIMP", /\bgimp\b/i],
  ["Canva", /\bcanva\b/i],
  ["Pixelmator", /pixelmator/i],
];

const getExtension = (name = "") => name.split(".").pop().toLowerCase();

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(
    units.length - 1,
    Math.floor(Math.log(bytes) / Math.log(1024))
  );
  return `${(bytes / 1024 ** index).toFixed(index ? 2 : 0)} ${units[index]}`;
};

const safeValue = (value, depth = 0) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (ArrayBuffer.isView(value)) {
    return `[Binary data: ${value.byteLength.toLocaleString()} bytes]`;
  }
  if (value instanceof ArrayBuffer) {
    return `[Binary data: ${value.byteLength.toLocaleString()} bytes]`;
  }
  if (Array.isArray(value)) {
    const items = value.slice(0, 40).map((item) => safeValue(item, depth + 1));
    return `${items.join(", ")}${value.length > 40 ? ` … (+${value.length - 40})` : ""}`;
  }
  if (typeof value === "object") {
    if (depth > 3) return "[Nested object]";
    try {
      const entries = Object.entries(value).slice(0, 40);
      return `${entries
        .map(([key, item]) => `${key}: ${safeValue(item, depth + 1)}`)
        .join("; ")}${Object.keys(value).length > 40 ? " …" : ""}`;
    } catch {
      return "[Unreadable value]";
    }
  }
  return String(value);
};

const readTagValue = (tag) => {
  if (tag === null || tag === undefined) return "";
  if (typeof tag !== "object" || Array.isArray(tag) || ArrayBuffer.isView(tag)) {
    return safeValue(tag);
  }
  if (tag.description !== undefined && tag.description !== "") {
    return safeValue(tag.description);
  }
  if (tag.value !== undefined) return safeValue(tag.value);
  return safeValue(tag);
};

const flattenMetadata = (metadata) => {
  const rows = [];

  const walk = (value, path = [], group = "Other") => {
    if (value === null || value === undefined) return;

    const isLeafTag =
      typeof value === "object" &&
      !Array.isArray(value) &&
      !ArrayBuffer.isView(value) &&
      (Object.prototype.hasOwnProperty.call(value, "description") ||
        Object.prototype.hasOwnProperty.call(value, "value"));

    if (isLeafTag) {
      const tag = path[path.length - 1] || "Value";
      const displayValue = readTagValue(value);
      if (displayValue) {
        rows.push({
          group,
          tag,
          path: path.join("."),
          value: displayValue,
        });
      }
      return;
    }

    if (
      typeof value !== "object" ||
      Array.isArray(value) ||
      ArrayBuffer.isView(value) ||
      value instanceof ArrayBuffer
    ) {
      const displayValue = safeValue(value);
      if (displayValue) {
        rows.push({
          group,
          tag: path[path.length - 1] || "Value",
          path: path.join("."),
          value: displayValue,
        });
      }
      return;
    }

    Object.entries(value).forEach(([key, item]) => {
      const nextPath = [...path, key];
      const nextGroup = path.length === 0 ? key.toUpperCase() : group;
      walk(item, nextPath, nextGroup);
    });
  };

  walk(metadata);
  return rows;
};

const findSignals = (text, patterns) => {
  const found = [];
  patterns.forEach(([label, pattern]) => {
    if (pattern.test(text) && !found.includes(label)) found.push(label);
  });
  return found;
};

const analyzeEvidence = (rows, c2paResult) => {
  const metadataText = rows
    .map((row) => `${row.path} ${row.tag} ${row.value}`)
    .join("\n");
  const credentialText = c2paResult?.storeText || "";
  const metadataAiSignals = findSignals(metadataText, STRONG_AI_PATTERNS);
  const credentialAiSignals = findSignals(credentialText, STRONG_AI_PATTERNS);
  const editorSignals = findSignals(metadataText, EDITOR_PATTERNS);
  const hasCredentials = Boolean(c2paResult?.hasManifest);

  if (hasCredentials && credentialAiSignals.length) {
    return {
      status: "credential-ai",
      title: "AI provenance found in Content Credentials",
      message:
        "The embedded C2PA manifest contains an AI-related provenance signal. Review the credential details and validation status below.",
      color: COLORS.green,
      background: COLORS.greenSoft,
      metadataAiSignals,
      credentialAiSignals,
      editorSignals,
    };
  }

  if (metadataAiSignals.length) {
    return {
      status: "metadata-ai",
      title: "AI-related metadata found",
      message:
        "The file contains one or more AI-related metadata signals. Ordinary metadata can be edited, so this is evidence—not absolute proof.",
      color: COLORS.yellow,
      background: COLORS.yellowSoft,
      metadataAiSignals,
      credentialAiSignals,
      editorSignals,
    };
  }

  if (hasCredentials) {
    return {
      status: "credential-other",
      title: "Content Credentials found",
      message:
        "A C2PA manifest was found, but this reader did not find an explicit AI-generation signal in its text representation.",
      color: COLORS.blue,
      background: COLORS.blueSoft,
      metadataAiSignals,
      credentialAiSignals,
      editorSignals,
    };
  }

  if (rows.length) {
    return {
      status: "no-ai-signal",
      title: "No AI metadata signal found",
      message:
        "Readable metadata exists, but no recognized AI signal was found. This does not prove that the image is non-AI; metadata may be absent, removed, or changed.",
      color: COLORS.muted,
      background: COLORS.bg,
      metadataAiSignals,
      credentialAiSignals,
      editorSignals,
    };
  }

  return {
    status: "no-metadata",
    title: "No readable metadata found",
    message:
      "The image may have been exported, optimized, screenshotted, or stripped of metadata. Its origin cannot be determined from metadata.",
    color: COLORS.muted,
    background: COLORS.bg,
    metadataAiSignals,
    credentialAiSignals,
    editorSignals,
  };
};

const detectCoordinates = (rows) => {
  const candidates = rows.filter((row) =>
    /gps|latitude|longitude/i.test(`${row.path} ${row.tag}`)
  );
  const latitudeRow = candidates.find((row) => /latitude(?!ref)/i.test(row.tag));
  const longitudeRow = candidates.find((row) => /longitude(?!ref)/i.test(row.tag));
  const latitude = latitudeRow ? Number.parseFloat(latitudeRow.value) : NaN;
  const longitude = longitudeRow ? Number.parseFloat(longitudeRow.value) : NaN;
  return Number.isFinite(latitude) && Number.isFinite(longitude)
    ? { latitude, longitude }
    : null;
};

const safeJsonStringify = (value) => {
  const seen = new WeakSet();
  return JSON.stringify(
    value,
    (_, item) => {
      if (ArrayBuffer.isView(item)) {
        return `[Binary data: ${item.byteLength} bytes]`;
      }
      if (item instanceof ArrayBuffer) {
        return `[Binary data: ${item.byteLength} bytes]`;
      }
      if (item && typeof item === "object") {
        if (seen.has(item)) return "[Circular reference]";
        seen.add(item);
      }
      return item;
    },
    2
  );
};

const AIImageMetadataReader = () => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileInfo, setFileInfo] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [imageDimensions, setImageDimensions] = useState(null);
  const [rows, setRows] = useState([]);
  const [c2paResult, setC2paResult] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [activeGroup, setActiveGroup] = useState("ALL");

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const reset = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
    setFileInfo(null);
    setImageDimensions(null);
    setRows([]);
    setC2paResult(null);
    setAnalysis(null);
    setError("");
    setSearch("");
    setActiveGroup("ALL");
    setLoading(false);
    if (inputRef.current) inputRef.current.value = "";
    console.log("[AI IMAGE METADATA] Reader reset.");
  }, [previewUrl]);

  const readC2pa = async (file) => {
    let reader;
    try {
      const c2pa = await createC2pa();
      reader = await c2pa.reader.fromBlob(
        file.type || "application/octet-stream",
        file
      );
      const manifestStore = await reader.manifestStore();
      const storeText = safeJsonStringify(manifestStore || {});
      const hasManifest = Boolean(
        manifestStore &&
          typeof manifestStore === "object" &&
          Object.keys(manifestStore).length
      );
      const validationRows = [];

      const collectValidation = (value, path = []) => {
        if (!value || typeof value !== "object") return;
        Object.entries(value).forEach(([key, item]) => {
          const nextPath = [...path, key];
          if (/validation[_ ]?status|validation[_ ]?results?/i.test(key)) {
            validationRows.push({ path: nextPath.join("."), value: safeValue(item) });
          }
          if (path.length < 6) collectValidation(item, nextPath);
        });
      };
      collectValidation(manifestStore);

      return { hasManifest, manifestStore, storeText, validationRows, error: "" };
    } catch (c2paError) {
      console.info("[AI IMAGE METADATA] No readable C2PA manifest.", {
        message: c2paError?.message || String(c2paError),
      });
      return {
        hasManifest: false,
        manifestStore: null,
        storeText: "",
        validationRows: [],
        error: "No readable C2PA Content Credentials were found.",
      };
    } finally {
      if (reader) {
        try {
          await reader.free();
        } catch (freeError) {
          console.warn("[AI IMAGE METADATA] C2PA reader cleanup warning.", freeError);
        }
      }
    }
  };

  const handleFile = async (file) => {
    setError("");
    if (!file) return;

    const extension = getExtension(file.name);
    if (!ACCEPTED_EXTENSIONS.includes(extension)) {
      const message = `Unsupported file type .${extension || "unknown"}.`;
      setError(message);
      toast.error(message);
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      const message = "The image is larger than the 25 MB limit.";
      setError(message);
      toast.error(message);
      return;
    }

    setLoading(true);
    setRows([]);
    setC2paResult(null);
    setAnalysis(null);
    setSearch("");
    setActiveGroup("ALL");

    if (previewUrl) URL.revokeObjectURL(previewUrl);
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    setFileInfo({
      name: file.name,
      type: file.type || `image/${extension}`,
      size: file.size,
      lastModified: file.lastModified
        ? new Date(file.lastModified).toLocaleString()
        : "Unknown",
    });
    setImageDimensions(null);

    const image = new Image();
    image.onload = () =>
      setImageDimensions({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () =>
      console.info("[AI IMAGE METADATA] Browser preview unavailable for this format.");
    image.src = objectUrl;

    console.log("[AI IMAGE METADATA] Reading local image.", {
      name: file.name,
      type: file.type,
      size: file.size,
    });

    try {
      const [exifResult, credentials] = await Promise.all([
        ExifReader.load(file, { expanded: true, async: true }).catch((exifError) => {
          console.info("[AI IMAGE METADATA] No readable standard metadata.", {
            message: exifError?.message || String(exifError),
          });
          return {};
        }),
        readC2pa(file),
      ]);
      const flattened = flattenMetadata(exifResult || {});
      const evidence = analyzeEvidence(flattened, credentials);

      setRows(flattened);
      setC2paResult(credentials);
      setAnalysis(evidence);
      console.log("[AI IMAGE METADATA] Analysis complete.", {
        metadataFields: flattened.length,
        c2paManifest: credentials.hasManifest,
        status: evidence.status,
        aiSignals: [
          ...evidence.metadataAiSignals,
          ...evidence.credentialAiSignals,
        ],
      });
      toast.success("Image metadata analyzed.");
    } catch (readError) {
      console.error("[AI IMAGE METADATA] Analysis failed.", readError);
      setError(`Could not analyze this image: ${readError.message || "Unknown error"}`);
      toast.error("Could not analyze this image.");
    } finally {
      setLoading(false);
    }
  };

  const onDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    handleFile(event.dataTransfer.files?.[0]);
  };

  const groups = useMemo(
    () => ["ALL", ...Array.from(new Set(rows.map((row) => row.group))).sort()],
    [rows]
  );

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      const inGroup = activeGroup === "ALL" || row.group === activeGroup;
      const matches =
        !term ||
        `${row.group} ${row.tag} ${row.path} ${row.value}`
          .toLowerCase()
          .includes(term);
      return inGroup && matches;
    });
  }, [rows, activeGroup, search]);

  const coordinates = useMemo(() => detectCoordinates(rows), [rows]);
  const allSignals = analysis
    ? Array.from(
        new Set([
          ...analysis.credentialAiSignals,
          ...analysis.metadataAiSignals,
        ])
      )
    : [];

  const reportData = useMemo(
    () => ({
      file: fileInfo,
      dimensions: imageDimensions,
      result: analysis
        ? {
            status: analysis.status,
            title: analysis.title,
            message: analysis.message,
            aiSignals: allSignals,
            editorSignals: analysis.editorSignals,
          }
        : null,
      contentCredentials: c2paResult
        ? {
            found: c2paResult.hasManifest,
            validation: c2paResult.validationRows,
            manifest: c2paResult.manifestStore,
          }
        : null,
      metadataFields: rows,
    }),
    [fileInfo, imageDimensions, analysis, allSignals, c2paResult, rows]
  );

  const copyReport = async () => {
    try {
      await navigator.clipboard.writeText(safeJsonStringify(reportData));
      toast.success("Metadata report copied.");
    } catch (copyError) {
      console.error("[AI IMAGE METADATA] Copy failed.", copyError);
      toast.error("Could not copy the report.");
    }
  };

  const downloadJson = () => {
    const url = URL.createObjectURL(
      new Blob([safeJsonStringify(reportData)], {
        type: "application/json;charset=utf-8",
      })
    );
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${fileInfo?.name || "image"}-metadata.json`;
    anchor.click();
    URL.revokeObjectURL(url);
    console.log("[AI IMAGE METADATA] JSON downloaded.");
    toast.success("JSON downloaded.");
  };

  const downloadCsv = () => {
    const escapeCsv = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const csvRows = [
      ["Group", "Tag", "Path", "Value"],
      ...rows.map((row) => [row.group, row.tag, row.path, row.value]),
    ];
    const csv = csvRows.map((row) => row.map(escapeCsv).join(",")).join("\n");
    const url = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8" })
    );
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${fileInfo?.name || "image"}-metadata.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
    console.log("[AI IMAGE METADATA] CSV downloaded.");
    toast.success("CSV downloaded.");
  };

  return (
    <div style={{ background: COLORS.bg, color: COLORS.ink, minHeight: "100vh" }}>
      <ToastContainer position="top-right" autoClose={2600} />
      <div className="container py-5" style={{ Width: 1160 }}>
        <div className="text-center mb-4">
          <span
            className="badge rounded-pill mb-3"
            style={{ background: COLORS.accentSoft, color: COLORS.accent }}
          >
            PRIVATE • BROWSER-ONLY
          </span>
         
        </div>

        {!fileInfo && (
          <div
            role="button"
            tabIndex="0"
            className="card border-0 shadow-sm text-center"
            onDragEnter={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") inputRef.current?.click();
            }}
            style={{
              borderRadius: 20,
              cursor: "pointer",
              outline: dragging ? `3px solid ${COLORS.accent}` : "none",
              background: dragging ? COLORS.accentSoft : COLORS.panel,
            }}
          >
            <div className="card-body py-5 px-3">
              <div
                className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                style={{ width: 72, height: 72, background: COLORS.accentSoft, color: COLORS.accent }}
              >
                <FaUpload size={28} />
              </div>
              <h2 className="h4 fw-bold">Drop an image here</h2>
              <p style={{ color: COLORS.muted }}>or click to select a local file</p>
              <div className="small" style={{ color: COLORS.muted }}>
                JPEG, JXL, PNG, WebP, GIF, TIFF, HEIC, HEIF or AVIF • Maximum 25 MB
              </div>
              <input
                ref={inputRef}
                type="file"
                accept=".jpg,.jpeg,.jxl,.png,.webp,.gif,.tif,.tiff,.heic,.heif,.avif,image/*"
                className="d-none"
                onChange={(event) => handleFile(event.target.files?.[0])}
              />
            </div>
          </div>
        )}

        {error && (
          <div className="alert mt-3 mb-0" style={{ background: COLORS.accentSoft, color: COLORS.accent }}>
            <FaExclamationTriangle className="me-2" />{error}
          </div>
        )}

        {loading && (
          <div className="card border-0 shadow-sm mt-4" style={{ borderRadius: 18 }}>
            <div className="card-body text-center py-5">
              <div className="spinner-border mb-3" role="status" style={{ color: COLORS.accent }} />
              <h2 className="h5 fw-bold">Reading local metadata…</h2>
              <p className="mb-0" style={{ color: COLORS.muted }}>
                Standard metadata and Content Credentials are being analyzed in this browser.
              </p>
            </div>
          </div>
        )}

        {fileInfo && !loading && analysis && (
          <>
            <div className="row g-4 mt-1">
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 18 }}>
                  <div className="card-body p-3">
                    <div className="rounded-3 overflow-hidden mb-3 d-flex align-items-center justify-content-center" style={{ minHeight: 250, background: COLORS.dark }}>
                      <img
                        src={previewUrl}
                        alt={`Preview of ${fileInfo.name}`}
                        className="img-fluid"
                        style={{ maxHeight: 360, objectFit: "contain" }}
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                      {!imageDimensions && <FaFileImage size={54} color="#77737F" />}
                    </div>
                    <div className="fw-bold text-break mb-2">{fileInfo.name}</div>
                    <div className="small d-flex justify-content-between py-1"><span style={{ color: COLORS.muted }}>Type</span><strong>{fileInfo.type}</strong></div>
                    <div className="small d-flex justify-content-between py-1"><span style={{ color: COLORS.muted }}>Size</span><strong>{formatBytes(fileInfo.size)}</strong></div>
                    {imageDimensions && (
                      <>
                        <div className="small d-flex justify-content-between py-1"><span style={{ color: COLORS.muted }}>Dimensions</span><strong>{imageDimensions.width} × {imageDimensions.height}</strong></div>
                        <div className="small d-flex justify-content-between py-1"><span style={{ color: COLORS.muted }}>Megapixels</span><strong>{((imageDimensions.width * imageDimensions.height) / 1_000_000).toFixed(2)} MP</strong></div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-8">
                <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 18 }}>
                  <div className="card-body p-4">
                    <div className="d-flex align-items-start gap-3">
                      <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 46, height: 46, background: analysis.background, color: analysis.color }}>
                        {analysis.status === "credential-ai" ? <FaShieldAlt /> : analysis.status.includes("ai") ? <FaExclamationTriangle /> : <FaCheckCircle />}
                      </div>
                      <div>
                        <div className="small text-uppercase fw-bold mb-1" style={{ color: analysis.color }}>Metadata-based result</div>
                        <h2 className="h4 fw-bold mb-2">{analysis.title}</h2>
                        <p className="mb-0" style={{ color: COLORS.muted }}>{analysis.message}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-sm-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 15 }}><div className="card-body"><div className="small text-uppercase fw-bold" style={{ color: COLORS.muted }}>Metadata fields</div><div className="fs-3 fw-bold mt-1">{rows.length.toLocaleString()}</div></div></div>
                  </div>
                  <div className="col-sm-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 15 }}><div className="card-body"><div className="small text-uppercase fw-bold" style={{ color: COLORS.muted }}>AI signals</div><div className="fs-3 fw-bold mt-1">{allSignals.length}</div></div></div>
                  </div>
                  <div className="col-sm-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 15 }}><div className="card-body"><div className="small text-uppercase fw-bold" style={{ color: COLORS.muted }}>Content Credentials</div><div className="fs-5 fw-bold mt-2">{c2paResult?.hasManifest ? "Found" : "Not found"}</div></div></div>
                  </div>
                </div>
              </div>
            </div>

            {(allSignals.length > 0 || analysis.editorSignals.length > 0 || coordinates) && (
              <div className="card border-0 shadow-sm mt-4" style={{ borderRadius: 18 }}>
                <div className="card-body p-4">
                  <h2 className="h5 fw-bold mb-3">Detected signals</h2>
                  <div className="d-flex flex-wrap gap-2">
                    {allSignals.map((signal) => <span key={signal} className="badge rounded-pill" style={{ background: COLORS.yellowSoft, color: COLORS.yellow, fontSize: ".82rem" }}>{signal}</span>)}
                    {analysis.editorSignals.map((signal) => <span key={signal} className="badge rounded-pill" style={{ background: COLORS.blueSoft, color: COLORS.blue, fontSize: ".82rem" }}>Editor: {signal}</span>)}
                    {coordinates && <a className="badge rounded-pill text-decoration-none" target="_blank" rel="noreferrer" href={`https://www.google.com/maps?q=${coordinates.latitude},${coordinates.longitude}`} style={{ background: COLORS.accentSoft, color: COLORS.accent, fontSize: ".82rem" }}><FaMapMarkerAlt className="me-1" />Sensitive GPS location found</a>}
                  </div>
                </div>
              </div>
            )}

            {c2paResult?.hasManifest && (
              <div className="card border-0 shadow-sm mt-4" style={{ borderRadius: 18 }}>
                <div className="card-body p-4">
                  <h2 className="h5 fw-bold mb-2"><FaShieldAlt className="me-2" style={{ color: COLORS.green }} />C2PA Content Credentials</h2>
                  <p className="small mb-3" style={{ color: COLORS.muted }}>A manifest was returned by the official browser SDK. Review validation entries and the JSON export for full details.</p>
                  {c2paResult.validationRows.length ? c2paResult.validationRows.map((item) => <div className="small border-top py-2" key={item.path}><strong>{item.path}</strong><div className="text-break" style={{ color: COLORS.muted }}>{item.value}</div></div>) : <div className="small rounded-3 p-3" style={{ background: COLORS.yellowSoft, color: COLORS.yellow }}>No separate validation-status field was exposed in the parsed manifest object. Do not treat manifest presence alone as proof of validity.</div>}
                </div>
              </div>
            )}

            <div className="card border-0 shadow-sm mt-4" style={{ borderRadius: 18 }}>
              <div className="card-body p-3 p-md-4">
                <div className="d-flex flex-wrap justify-content-between gap-3 align-items-center mb-3">
                  <div>
                    <h2 className="h4 fw-bold mb-1">Metadata explorer</h2>
                    <div className="small" style={{ color: COLORS.muted }}>{visibleRows.length} of {rows.length} fields shown</div>
                  </div>
                  <div className="position-relative" style={{ minWidth: 260 }}>
                    <FaSearch className="position-absolute" style={{ left: 12, top: 12, color: COLORS.muted }} />
                    <input className="form-control ps-5" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search tags or values..." />
                  </div>
                </div>

                <div className="d-flex flex-wrap gap-2 mb-3">
                  {groups.map((group) => <button key={group} type="button" className="btn btn-sm" onClick={() => setActiveGroup(group)} style={{ background: activeGroup === group ? COLORS.dark : COLORS.bg, color: activeGroup === group ? "#fff" : COLORS.ink, fontWeight: 700 }}>{group}</button>)}
                </div>

                {visibleRows.length ? (
                  <div className="table-responsive" style={{ maxHeight: 520 }}>
                    <table className="table table-hover align-middle mb-0">
                      <thead className="position-sticky top-0" style={{ background: COLORS.panel, zIndex: 1 }}><tr><th>Group</th><th>Tag</th><th>Value</th></tr></thead>
                      <tbody>{visibleRows.map((row, index) => <tr key={`${row.path}-${index}`}><td><span className="badge" style={{ background: COLORS.bg, color: COLORS.ink }}>{row.group}</span></td><td className="fw-semibold">{row.tag}</td><td className="text-break" style={{ minWidth: 300 }}>{row.value}</td></tr>)}</tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-5" style={{ color: COLORS.muted }}>No matching metadata fields.</div>
                )}
              </div>
            </div>

            <div className="d-flex flex-wrap gap-2 mt-4">
              <button type="button" className="btn text-white" onClick={copyReport} style={{ background: COLORS.accent, fontWeight: 700 }}><FaCopy className="me-2" />Copy report</button>
              <button type="button" className="btn btn-outline-dark" onClick={downloadJson}><FaDownload className="me-2" />Download JSON</button>
              <button type="button" className="btn btn-outline-dark" onClick={downloadCsv}><FaDownload className="me-2" />Download CSV</button>
              <button type="button" className="btn btn-light" onClick={reset}><FaRedo className="me-2" />Read another image</button>
            </div>

            <div className="rounded-3 p-3 mt-4 small" style={{ background: COLORS.yellowSoft, color: "#694400" }}>
              <FaExclamationTriangle className="me-2" />
              <strong>Accuracy notice:</strong> no metadata signal is not proof that an image is non-AI. Metadata may be stripped, edited, missing, or unsupported. This tool performs metadata and provenance inspection, not pixel-level AI detection.
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AIImageMetadataReader;
